import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "@/admin/api";
import { PageTitle, StatusBadge, fmtDate } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Search, Download, Loader2, Trash2, Mail, Phone, Save } from "lucide-react";
import { toast } from "sonner";
import { SpamFolder } from "@/admin/components/SpamFolder";

const STATUSES = [
  "New", "Reviewed", "Contacted", "Waiting for Client", "Availability Sent",
  "Deposit Pending", "Confirmed", "Closed", "Not a Fit",
];
const INTERESTS = ["Resort Stay", "Cruise", "Group Travel", "Travel Deals", "Travel Essentials"];

export default function Inquiries() {
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ search: "", status: "all", travel_interest: "all", destination: "", date_from: "", date_to: "" });
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState({ status: "", admin_notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const { data } = await api.get("/admin/inquiries", { params });
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (it) => {
    setSelected(it);
    setDraft({ status: it.status, admin_notes: it.admin_notes || "" });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/inquiries/${selected.id}`, draft);
      toast.success("Inquiry updated");
      setSelected(data);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this inquiry? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      toast.success("Inquiry removed");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/inquiries/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "kisliving_inquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-inquiries">
      <PageTitle title="Travel Inquiries" subtitle="Manage and follow up on all form submissions">
        <Button data-testid="export-inquiries" onClick={exportCsv} variant="outline" className="rounded-full border-ink/20 gap-2">
          <Download size={16} /> Export CSV
        </Button>
      </PageTitle>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-bone p-4 mb-5 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="relative md:col-span-2">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
          <Input data-testid="inquiry-search" value={filters.search} onChange={(e) => set("search", e.target.value)}
            placeholder="Search name, email, destination" className="pl-9 rounded-xl border-bone" />
        </div>
        <Select value={filters.status} onValueChange={(v) => set("status", v)}>
          <SelectTrigger data-testid="filter-status" className="rounded-xl border-bone"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.travel_interest} onValueChange={(v) => set("travel_interest", v)}>
          <SelectTrigger data-testid="filter-interest" className="rounded-xl border-bone"><SelectValue placeholder="Travel type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {INTERESTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="filter-destination" value={filters.destination} onChange={(e) => set("destination", e.target.value)}
          placeholder="Destination" className="rounded-xl border-bone" />
        <div className="md:col-span-5 flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-ink-soft whitespace-nowrap">From</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} className="rounded-xl border-bone" />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-ink-soft whitespace-nowrap">To</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} className="rounded-xl border-bone" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-bone overflow-hidden">
        {!items ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-terracotta" /></div>
        ) : items.length === 0 ? (
          <p className="text-center text-ink-soft py-16">No inquiries match your filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-sand text-ink-soft text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Interest</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Destination</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Date</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bone">
                {items.map((it) => (
                  <tr key={it.id} data-testid={`inquiry-row-${it.id}`} onClick={() => openDetail(it)}
                    className="cursor-pointer hover:bg-sand/60 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-ink">{it.first_name}</p>
                      <p className="text-xs text-ink-soft">{it.email}</p>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-ink-soft">{it.travel_interest || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-ink-soft">{it.destination || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-ink-soft">{fmtDate(it.created_at)}</td>
                    <td className="px-4 py-3"><StatusBadge status={it.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {items && <p className="text-xs text-ink-soft mt-3">{items.length} inquiries</p>}

      <SpamFolder onRestored={load} />

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent data-testid="inquiry-detail" className="w-full sm:max-w-lg overflow-y-auto bg-sand">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-serif text-2xl text-ink">{selected.first_name}</SheetTitle>
                <SheetDescription className="text-ink-soft text-sm">{selected.email}</SheetDescription>
              </SheetHeader>
              <div className="mt-5 space-y-5">
                <div className="bg-white rounded-xl border border-bone p-4 space-y-2 text-sm">
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-terracotta font-medium"><Mail size={15} /> {selected.email}</a>
                  {selected.phone && <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-ink"><Phone size={15} /> {selected.phone}</a>}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    ["Travel Interest", selected.travel_interest],
                    ["Destination", selected.destination],
                    ["Travel Month", selected.travel_month],
                    ["Group Size", selected.group_size],
                    ["Budget", selected.budget],
                    ["Source", selected.source],
                    ["Submitted", fmtDate(selected.created_at)],
                    ["Consent", selected.consent ? "Yes" : "No"],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-white rounded-xl border border-bone p-3">
                      <p className="text-xs text-ink-soft">{k}</p>
                      <p className="text-ink font-medium">{v || "—"}</p>
                    </div>
                  ))}
                </div>

                {selected.notes && (
                  <div className="bg-white rounded-xl border border-bone p-3 text-sm">
                    <p className="text-xs text-ink-soft mb-1">Client Message</p>
                    <p className="text-ink">{selected.notes}</p>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Label className="text-ink font-medium">Status</Label>
                  <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
                    <SelectTrigger data-testid="inquiry-status-select" className="rounded-xl border-bone bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-opt-${s.replace(/\s+/g, "-")}`}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2">
                  <Label className="text-ink font-medium">Internal Admin Notes</Label>
                  <Textarea data-testid="inquiry-notes" value={draft.admin_notes}
                    onChange={(e) => setDraft((d) => ({ ...d, admin_notes: e.target.value }))}
                    placeholder="Notes visible to admins only" className="rounded-xl border-bone bg-white min-h-[100px]" />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button data-testid="inquiry-save" onClick={save} disabled={saving}
                    className="flex-1 rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">
                    {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} /> Save Changes</>}
                  </Button>
                  <Button data-testid="inquiry-delete" onClick={() => remove(selected.id)} variant="outline"
                    className="rounded-full border-red-200 text-red-600 hover:bg-red-50">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
